import { Head, usePage } from "@inertiajs/react"
import ogDefault from "@/images/og-default.jpg"
import type { PageProps } from "@/types/inertia"

// Page metadata for every public page (M7 decision 15): the four tags every
// page needs (title, description, og:title, og:description) plus the social
// card (og:type, og:site_name, og:image at 1200 × 630, twitter:card) and,
// on Home, the JSON-LD. The share image is built from Duncan's Salt House
// bottle (app/frontend/images/og-default.jpg: JPEG, which every link
// unfurler reads).
//
// Inertia's <Head> only reads plain elements, never components, so this
// renders the whole <Head> itself: use it in place of <Head>, not inside it.

export const SITE_NAME = "SOH GD Wines"

const TITLE_LIMIT = 60
const OG_IMAGE_ALT =
  "A bottle of South African red wine with a bespoke \"Just Married\" label, beside the words Premium South African wine. Your brand on the bottle."

export type JsonLd = Record<string, unknown>

/**
 * The document title: "Page | SOH GD Wines" while that stays within 60
 * characters, else the page title alone (long wine names). `brand={false}`
 * keeps a title that is already keyword-led (Home) as it is.
 */
export function documentTitle(title: string, brand = true) {
  const branded = `${title} | ${SITE_NAME}`
  return brand && branded.length <= TITLE_LIMIT ? branded : title
}

/** An absolute URL for a path on this site, when the origin is known. */
function absolute(origin: string | undefined, path: string) {
  if (/^https?:\/\//.test(path)) return path
  return origin ? `${origin.replace(/\/$/, "")}${path.startsWith("/") ? "" : "/"}${path}` : path
}

/** JSON for a <script type="application/ld+json">, safe inside HTML. */
function serialise(data: JsonLd) {
  return JSON.stringify(data).replace(/</g, "\\u003c")
}

export function PublicMeta({
  title,
  description,
  ogTitle,
  ogDescription,
  brand = true,
  jsonLd,
}: {
  /** The page's own title, without the site name. */
  title: string
  /** 120–160 characters, unique to the page. */
  description: string
  /** Social copy, when it should differ from the search copy. */
  ogTitle?: string
  ogDescription?: string
  brand?: boolean
  jsonLd?: JsonLd
}) {
  const { props } = usePage<PageProps>()
  const imageUrl = absolute(props.site_url, ogDefault)

  return (
    <Head title={documentTitle(title, brand)}>
      <meta name="description" content={description} />
      <meta property="og:title" content={ogTitle ?? title} />
      <meta property="og:description" content={ogDescription ?? description} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={OG_IMAGE_ALT} />
      <meta name="twitter:card" content="summary_large_image" />
      {jsonLd ? (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serialise(jsonLd) }} />
      ) : null}
    </Head>
  )
}

/**
 * Home's structured data: the business (Organization) and the site
 * (WebSite), from facts on the site only. The logo is the site icon
 * (public/icon.png, 512 × 512).
 */
export function homeJsonLd(origin: string | undefined, description: string): JsonLd {
  const url = absolute(origin, "/")
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${url}#organization`,
        name: SITE_NAME,
        url,
        description,
        image: absolute(origin, ogDefault),
        logo: absolute(origin, "/icon.png"),
        areaServed: { "@type": "Country", name: "United Kingdom" },
      },
      {
        "@type": "WebSite",
        "@id": `${url}#website`,
        name: SITE_NAME,
        url,
        inLanguage: "en-GB",
        publisher: { "@id": `${url}#organization` },
      },
    ],
  }
}
